<template>
    <v-row class="pa-2" no-gutters>
        <!-- Player list -->
        <v-col cols="12" md="3" class="pr-md-3 mb-3">
            <div class="d-flex align-center mb-2" style="gap: 6px">
                <v-text-field v-model="query" label="Search players" density="compact" hide-details clearable
                    prepend-inner-icon="mdi-magnify" />
                <v-btn icon="mdi-refresh" size="small" variant="text" :loading="loadingList" title="Refresh" @click="loadList" />
            </div>
            <v-alert v-if="listError" type="error" variant="tonal" density="compact">{{ listError }}</v-alert>
            <div v-else-if="!players.length && !loadingList" class="text-caption text-grey">
                No players recorded yet. Gear is saved for every player that appears near you.
            </div>
            <v-list density="compact" class="bg-transparent pa-0 gear-list thin-scroll">
                <v-list-item v-for="p in filtered" :key="p.name" :active="p.name === selected"
                    rounded="sm" @click="select(p.name)">
                    <v-list-item-title class="text-body-2">
                        {{ anonymizeMode ? playerAlias(p) : p.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                        {{ raceNameMap?.[p.raceId] ?? '' }}<span v-if="p.guild && !anonymizeMode"> · {{ p.guild }}</span>
                        · {{ ago(p.lastSeen) }}
                    </v-list-item-subtitle>
                </v-list-item>
            </v-list>
        </v-col>

        <!-- Selected player's gear -->
        <v-col cols="12" md="9">
            <div v-if="!selected" class="text-caption text-grey pa-2">Select a player to see their last known gear.</div>
            <template v-else>
                <div class="d-flex align-center mb-2" style="gap: 8px">
                    <span class="text-subtitle-1 font-weight-bold">{{ anonymizeMode ? 'Player' : gear?.name }}</span>
                    <span v-if="gear" class="text-caption text-grey">last seen {{ ago(gear.lastSeen) }}</span>
                    <v-progress-circular v-if="loadingGear" indeterminate size="16" width="2" />
                </div>
                <v-alert v-if="gearError" type="error" variant="tonal" density="compact">{{ gearError }}</v-alert>
                <div v-else-if="gear && !gear.items.length" class="text-caption text-grey">No gear recorded.</div>
                <equip-board v-else-if="gear" :items="gear.items" />
                <div v-if="gear?.items.length" class="text-caption text-grey mt-2">
                    Other players show item and dyes only; full stats are known for your own characters.
                </div>
            </template>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, ref, computed, inject, onMounted, type Ref } from 'vue';
import type { EquipmentItem } from '@/items/itemIndex';
import { anonymizeMode } from '@/store';
import EquipBoard from './subComponents/EquipBoard.vue';

interface PlayerSummary { name: string; entityId: string; raceId: number; guild: string; lastSeen: number; itemCount: number }
interface PlayerGearData extends PlayerSummary { items: EquipmentItem[] }

// Last known gear of every player seen nearby, from /api/players/gear
// (equipment.db, keyed by character name).
export default defineComponent({
    components: { EquipBoard },
    setup() {
        const raceNameMap = inject('raceNameMap') as Ref<Record<number, string>> | undefined;
        const players = ref<PlayerSummary[]>([]);
        const loadingList = ref(false);
        const listError = ref('');
        const query = ref('');
        const selected = ref('');
        const gear = ref<PlayerGearData | null>(null);
        const loadingGear = ref(false);
        const gearError = ref('');

        const loadList = async () => {
            loadingList.value = true;
            listError.value = '';
            try {
                const r = await fetch('/api/players/gear');
                if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
                players.value = await r.json();
            } catch (e) {
                listError.value = `Failed to load players: ${(e as Error).message}`;
            } finally {
                loadingList.value = false;
            }
            if (selected.value) void select(selected.value);
        };

        const select = async (name: string) => {
            selected.value = name;
            loadingGear.value = true;
            gearError.value = '';
            try {
                const r = await fetch(`/api/players/gear?name=${encodeURIComponent(name)}`);
                if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
                const data = await r.json() as PlayerGearData;
                if (selected.value === name) gear.value = data;
            } catch (e) {
                if (selected.value === name) gearError.value = `Failed to load gear: ${(e as Error).message}`;
            } finally {
                if (selected.value === name) loadingGear.value = false;
            }
        };

        const filtered = computed(() => {
            const q = (query.value ?? '').trim().toLowerCase();
            if (!q || anonymizeMode.value) return players.value;
            return players.value.filter(p => p.name.toLowerCase().includes(q) || p.guild.toLowerCase().includes(q));
        });

        // Anonymize mode: stable per-list labels instead of names.
        const playerAlias = (p: PlayerSummary) => `Player ${players.value.indexOf(p) + 1}`;

        const ago = (unix: number) => {
            const s = Math.max(0, Math.floor(Date.now() / 1000 - unix));
            if (s < 60) return 'just now';
            if (s < 3600) return `${Math.floor(s / 60)}m ago`;
            if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
            return new Date(unix * 1000).toLocaleDateString();
        };

        onMounted(loadList);
        return {
            raceNameMap, players, loadingList, listError, query, selected, gear, loadingGear, gearError,
            loadList, select, filtered, playerAlias, ago, anonymizeMode,
        };
    },
});
</script>

<style scoped>
.gear-list { max-height: 65vh; overflow-y: auto; }
.thin-scroll::-webkit-scrollbar { width: 6px; }
.thin-scroll::-webkit-scrollbar-thumb { background-color: rgba(255, 255, 255, 0.2); border-radius: 3px; }
</style>
